import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Icon } from '@/components/ui/Icon'
import { cn } from '@/lib/cn'
import '@/features/auth/hero.css'

const chips = ['attribution', 'bytes', 'twoFactor'] as const
const tickerItems = ['init', 'online', 'traffic', 'submitted', 'approved', 'countries'] as const

const particles = [
  { left: '12%', rise: '3.6s', delay: '0s' },
  { left: '28%', rise: '2.8s', delay: '1.1s' },
  { left: '47%', rise: '3.2s', delay: '0.5s' },
  { left: '63%', rise: '2.6s', delay: '1.8s' },
  { left: '81%', rise: '3.9s', delay: '0.9s' },
]

const delay = (value: string) => ({ '--hero-delay': value }) as CSSProperties

type AuthHeroProps = {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  headerLink?: { to: string; label: string }
  compact?: boolean
}

export function AuthHero({
  title,
  subtitle,
  children,
  footer,
  headerLink,
  compact = false,
}: AuthHeroProps) {
  const { t } = useTranslation()
  const Title = compact ? 'h1' : 'h2'

  return (
    <div className="hero-stage flex min-h-dvh flex-col text-ink">
      <div aria-hidden="true" className="hero-glow -top-40 -right-40 h-240 w-150" />
      <div
        aria-hidden="true"
        className="hero-glow hero-glow--alt -bottom-60 -left-40 h-200 w-160"
      />

      <header className="hero-header sticky top-0 z-20">
        <div className="mx-auto flex h-control-lg max-w-app items-center gap-3 px-4 sm:px-8 lg:px-12">
          <img src="/brand/logo-mark.svg" alt={t('app.logoAlt')} className="size-6" />
          <span className="font-display text-sm font-semibold tracking-label text-ink uppercase">
            {t('app.name')}
          </span>
          <span aria-hidden="true" className="hero-dot size-1.5 rounded-full bg-brand-bright" />
          <div className="ml-auto flex items-center gap-2">
            {headerLink === undefined ? null : (
              <Link
                to={headerLink.to}
                className="inline-flex h-control-xs items-center gap-1.5 rounded-full bg-primary-fill px-4 text-sm font-semibold text-primary-on-fill transition-colors duration-250 ease-brand hover:bg-primary-fill-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring max-sm:hidden"
              >
                {headerLink.label}
                <Icon name="chevronRight" className="size-4" />
              </Link>
            )}
          </div>
        </div>
        <div aria-hidden="true" className="hero-hairline" />
      </header>

      <main
        id="main"
        className={cn(
          'relative z-10 mx-auto grid w-full max-w-app flex-1 items-center gap-12 px-4 py-12 sm:px-8 lg:gap-16 lg:px-12 lg:py-20',
          !compact && 'lg:grid-cols-[1.1fr_0.9fr]',
        )}
      >
        {compact ? null : (
          <section className="flex max-w-xl flex-col gap-6">
            <p
              className="hero-in hero-eyebrow font-mono text-xs tracking-label text-ink-secondary uppercase"
              style={delay('0.08s')}
            >
              {t('auth.hero.eyebrow')}
            </p>

            <h1
              className="hero-in text-mobile-h1 text-ink sm:text-h2 lg:text-h1"
              style={delay('0.2s')}
            >
              {`${t('auth.hero.titleLead')} `}
              <span className="text-brand-bright">{t('auth.hero.titleAccent')}</span>
              {t('auth.hero.titleTail')}
              <span aria-hidden="true" className="hero-caret" />
            </h1>

            <p className="hero-in max-w-lg text-base text-ink-secondary" style={delay('0.32s')}>
              {t('auth.hero.lead')}
            </p>

            <ul className="hero-in flex flex-wrap gap-2" style={delay('0.44s')}>
              {chips.map((chip) => (
                <li key={chip} className="hero-chip rounded-full px-3 py-1 font-mono text-xs">
                  {t(`auth.hero.chips.${chip}`)}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section
          aria-labelledby="auth-title"
          className={cn(
            'hero-in relative w-full max-w-md justify-self-center',
            !compact && 'lg:justify-self-end',
          )}
          style={delay('0.32s')}
        >
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-8 -top-4 h-56">
            {particles.map((particle) => (
              <span
                key={particle.left}
                className="hero-particle"
                style={
                  {
                    left: particle.left,
                    '--hero-rise': particle.rise,
                    '--hero-delay': particle.delay,
                  } as CSSProperties
                }
              />
            ))}
          </div>

          <div className="hero-card flex flex-col gap-6 rounded-xl p-6 sm:p-8">
            <div className="flex flex-col gap-1">
              <Title id="auth-title" className="text-2xl font-semibold text-ink">
                {title}
              </Title>
              {subtitle === undefined ? null : (
                <p className="text-sm text-ink-secondary">{subtitle}</p>
              )}
            </div>

            {children}
          </div>

          {footer === undefined ? null : (
            <div className="mt-4 text-center text-sm text-ink-secondary">{footer}</div>
          )}
        </section>
      </main>

      <div aria-hidden="true" className="hero-ticker relative z-10 overflow-hidden py-3">
        <div className="hero-ticker-track">
          {[0, 1].map((copy) => (
            <ul
              key={copy}
              className="flex shrink-0 gap-12 pr-12 font-mono text-xs whitespace-nowrap text-ink-muted"
            >
              {[...tickerItems, ...tickerItems].map((item, index) => (
                <li key={`${item}-${String(index)}`}>
                  <span className="text-brand-bright">›</span> {t(`auth.hero.ticker.${item}`)}
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>
    </div>
  )
}
